import React, { useEffect, useState } from 'react'
import i18n from 'simple-react-i18n'
import { SmallStyledFieldSet } from '../../../../components/StyledElements'
import { Grid, Tooltip, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import StepperDialog from 'components/modal/StepperDialog'
import { CardTable } from 'components/datatable/NewTable'
import { nbPerPageLabelShort } from 'referencial/constants/ReferencialConstants'
import { useDispatch } from 'react-redux'
import PerimeterStatus from 'components/PerimeterStatus'
import { camelCase, isNil, keys, lowerFirst, maxBy, orderBy, partition } from 'lodash'
import { ButtonMUI } from 'components/styled/Buttons'
import { darkBlue } from 'utils/constants/ColorTheme'
import StationAction from 'station/actions/StationAction'
import download from 'downloadjs'
import DtoFileFTP from 'station/dto/DtoFileFTP'
import IntegrationAction from 'import/actions/IntegrationAction'
import FileUploadInput from 'import/dto/FileUploadInput'
import RefJobAction from 'domain/job/RefJobAction'
import JobAction from 'import/actions/JobAction'
import { SUCCESS, WARNING } from 'import/constants/JobConstants'
import DtoJobLogLight from 'import/dto/DtoJobLogLight'
import { getFullDate } from 'utils/DateUtil'
import InfoRoundedIcon from '@mui/icons-material/InfoRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import SimpleCircularProgressWithLabel from 'components/progress/SimpleCircularProgressWithLabel'
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded'
import { hasValue } from 'utils/NumberUtil'
import ErrorIcon from '@mui/icons-material/Error'
import EditRoundedIcon from '@mui/icons-material/EditRounded'

const STEP = {
    SETTINGS: 0,
    STATUS: 1,
    SUCCESS: 2,
}
const IMPORT_STATUS = {
    0: 'orange',
    1: 'red',
}
const JOB_SUCCESS_STATUS = [1, 2, 4]
const IMPORT_PERIMETERS_JOB_ID = -21

const CHECK_UP_IMPORT_ERRORS = ['nullValue', 'line', 'perimeter', 'city', 'parcel', 'remark']

const DownloadModelImport = ({
    model,
    onClose = () => { },
}) => (
    <Grid container alignItems='center' rowSpacing={1}>
        <Grid size={12}>
            <Typography fontSize='14px' color='mainWhite'>{i18n.downloadModelFileMessage}</Typography>
        </Grid>
        <Grid container size={12} alignItems='center' justifyContent='space-between'>
            <Grid>
                <ButtonMUI
                    onClick={onClose}
                    variant='outlined'
                    color='mainWhite'
                >
                    {i18n.no}
                </ButtonMUI>
            </Grid>
            <Grid>
                <ButtonMUI
                    onClick={() => {
                        download(model)
                        onClose()
                    }}
                    variant='contained'
                    color='mainWhite'
                >
                    {i18n.yes}
                </ButtonMUI>
            </Grid>
        </Grid>
    </Grid>
)

DownloadModelImport.propTypes = {
    onClose: PropTypes.func,
    model: PropTypes.instanceOf(DtoFileFTP),
}

const ImportStep = ({
    onUploadFile = () => { },
}) => {
    const [openModelImportTooltip, setOpenModelImportTooltip] = useState(false)
    const [model, setModel] = useState()

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(StationAction.fetchFilesFtp('MODELES/PERITEC', 'PERIMETER_IMPORT_MODEL.xlsx')).then(files => {
            if (files[0]?.url) {
                setModel(files[0]?.url)
            }
        })
    }, [])

    return (
        <SmallStyledFieldSet>
            <Grid container direction='column' sx={{ padding: '1rem' }}>
                <Grid container columnSpacing={1} alignItems='center'>
                    <Grid>{i18n.selectExcelFileNotaryFormat}</Grid>
                    {!!model && (
                        <Grid>
                            <Tooltip
                                open={openModelImportTooltip}
                                placement='top-start'
                                title={(
                                    <DownloadModelImport
                                        onClose={() => setOpenModelImportTooltip(false)}
                                        model={model}
                                    />
                                )}
                                slotProps={{
                                    tooltip: {
                                        sx: {
                                            padding: '1rem',
                                            color: darkBlue,
                                            maxWidth: '335px',
                                        },
                                    },
                                }}
                            >
                                <InfoRoundedIcon sx={{ cursor: 'pointer' }} onClick={() => setOpenModelImportTooltip(true)} />
                            </Tooltip>
                        </Grid>
                    )}
                </Grid>
                <Grid>
                    <div className='file-field input-field'>
                        <div className='btn'>
                            <span>{i18n.importLabel}</span>
                            <input
                                accept='.xls, .xlsx'
                                type='file'
                                onChange={onUploadFile}
                            />
                        </div>
                        <div className='file-path-wrapper'>
                            <input className='file-path validate' type='text' style={{ margin: 0 }} />
                        </div>
                    </div>
                </Grid>
            </Grid>
        </SmallStyledFieldSet>
    )
}

ImportStep.propTypes = {
    onUploadFile: PropTypes.func,
}

const CheckUpStep = ({
    importLogs = [],
    job = {},
}) => {
    const formattedImportErrors = orderBy(importLogs, 'date', 'asc').map(ie => ({
        ...ie,
        nullValue: (
            <PerimeterStatus
                color={IMPORT_STATUS[ie.level]}
            />
        ),
        color: IMPORT_STATUS[ie.level],
        date: getFullDate(ie.date),
    })) || []

    return (
        <Grid container justifyContent='center' sx={{ padding: '1rem' }} rowSpacing={4}>
            {isNil(job.statusCode) && (
                <Grid container size={12} rowSpacing={2}>
                    <Grid container size={12} justifyContent='center'>
                        <Grid>
                            <SimpleCircularProgressWithLabel />
                        </Grid>
                    </Grid>
                    <Grid container size={12} justifyContent='center'>
                        <Grid>
                            <Typography fontSize='16px'>{i18n.checkingImportInProgress}</Typography>
                        </Grid>
                    </Grid>
                </Grid>
            )}
            {(!isNil(job.statusCode) && !JOB_SUCCESS_STATUS.includes(job.statusCode) && importLogs.some(il => il.level === 1)) && (
                <Grid container size={12} rowSpacing={1}>
                    <Grid container size={12} justifyContent='center'>
                        <Grid>
                            <ErrorIcon sx={{ color: 'red', fontSize: '5rem' }} />
                        </Grid>
                    </Grid>
                    <Grid container size={12} justifyContent='center'>
                        <Grid>
                            <Typography fontSize='16px'>{i18n.fileContainsErrors}</Typography>
                        </Grid>
                    </Grid>
                </Grid>
            )}
            <Grid size={12}>
                <CardTable
                    title={i18n.importControls}
                    rows={formattedImportErrors}
                    headers={CHECK_UP_IMPORT_ERRORS}
                    rowsPerPageOptions={nbPerPageLabelShort}
                />
            </Grid>
        </Grid>
    )
}

CheckUpStep.propTypes = {
    importLogs: PropTypes.arrayOf(PropTypes.shape({})),
    job: PropTypes.shape({}),
}

const ValidateStep = ({
    validateImport,
    importLogs,
}) => {
    const formattedImportErrors = orderBy(importLogs, 'date', 'asc').map(ie => ({
        ...ie,
        nullValue: (
            <PerimeterStatus
                color={IMPORT_STATUS[ie.level]}
            />
        ),
        color: IMPORT_STATUS[ie.level],
        date: getFullDate(ie.date),
    })) || []

    const [newKeys, updatedKeys] = partition(keys(validateImport), i => i.startsWith('new'))
    return (
        <Grid container justifyContent='center' sx={{ padding: '1rem' }} rowSpacing={8}>
            <Grid container size={12} rowSpacing={1}>
                <Grid container size={12} justifyContent='center'>
                    <Grid>
                        <CheckCircleRoundedIcon sx={{ color: 'green', fontSize: '5rem' }} />
                    </Grid>
                </Grid>
                <Grid container size={12} justifyContent='center'>
                    <Grid>
                        <Typography fontSize='1rem'>{i18n.importPerimetersValidateMessage}</Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container size={12} justifyContent='space-between' rowSpacing={2}>
                {newKeys.map(key => {
                    const nb = validateImport[key]
                    const formattedKey = nb > 1 ? key : key.slice(0, -1)
                    return (
                        <Grid container key={`Import_de_perimetres_${camelCase(key)}`} size={4} alignItems='center' justifyContent='center' columnSpacing={1}>
                            <Grid>
                                <AddCircleRoundedIcon sx={{ color: 'green', fontSize: '1rem' }} />
                            </Grid>
                            <Grid>
                                <Typography fontSize='1rem'>{nb} {lowerFirst(i18n[formattedKey])}</Typography>
                            </Grid>
                        </Grid>
                    )
                })}
                {updatedKeys.map(key => {
                    const nb = validateImport[key]
                    const formattedKey = nb > 1 ? key : key.slice(0, -1)
                    return (
                        <Grid container key={`Import_de_perimetres_${camelCase(key)}`} size={4} alignItems='center' justifyContent='center' columnSpacing={1}>
                            <Grid>
                                <EditRoundedIcon sx={{ color: 'green', fontSize: '1rem' }} />
                            </Grid>
                            <Grid>
                                <Typography fontSize='1rem'>{nb} {lowerFirst(i18n[formattedKey])}</Typography>
                            </Grid>
                        </Grid>
                    )
                })}
            </Grid>
            {(!importLogs.some(il => il.level === 1)) && (
                <Grid size={12}>
                    <CardTable
                        title={i18n.importControls}
                        rows={formattedImportErrors}
                        headers={CHECK_UP_IMPORT_ERRORS}
                        rowsPerPageOptions={nbPerPageLabelShort}
                    />
                </Grid>
            )}
        </Grid>
    )
}

ValidateStep.propTypes = {
    validateImport: PropTypes.shape({
        newParcels: PropTypes.number,
        updatedParcels: PropTypes.number,
        newOwnersAccounts: PropTypes.number,
        updatedOwnersAccounts: PropTypes.number,
        newOwners: PropTypes.number,
        updatedOwners: PropTypes.number,
    }),
    importLogs: PropTypes.arrayOf(PropTypes.shape({})),
}

const PerimetersImportStepper = ({
    folderId,
    open = false,
    setOpen = () => { },
    refreshData = () => { },
}) => {
    const [fileName, setFileName] = useState()
    const [prevFileContent, setPrevFileContent] = useState()
    const [fileContent, setFileContent] = useState()
    const [importLogs, setImportLogs] = useState([])
    const [validateImport, setValidateImport] = useState()
    const [job, setJob] = useState()

    const dispatch = useDispatch()

    useEffect(() => {
        if (!open) {
            setFileName()
            setPrevFileContent()
            setFileContent()
            setImportLogs([])
            setValidateImport()
            setJob()
        }
    }, [open])

    const onImportFile = (e) => {
        const reader = new FileReader()
        const file = e.target.files[0]
        reader.onload = upload => {
            setFileName(file.name)
            setFileContent(upload.target.result)
            setImportLogs([])
        }
        reader.readAsDataURL(file)
    }

    const onClose = () => setOpen(false)

    const automaticCallLogs = (nextStep = () => { }) => {
        RefJobAction.promiseJobExecutions(IMPORT_PERIMETERS_JOB_ID).then(res => {
            const jobExecutionId = maxBy(res, 'date').id
            if (jobExecutionId) {
                JobAction.requestJobLogs(-500, jobExecutionId).then(res2 => {
                    const jobLogs = res2.data.map(el => new DtoJobLogLight(el, jobExecutionId))
                    const errorJobLogs = jobLogs.filter(d => d.status === WARNING && d.value?.startsWith('{'))
                    const jobExecution = res.find(e => e.id === jobExecutionId)
                    setJob(jobExecution)
                    setImportLogs(errorJobLogs.map(l => JSON.parse(l.value)))
                    if (jobExecution?.statusCode === undefined) {
                        setTimeout(() => automaticCallLogs(nextStep), 2000)
                    } else if (JOB_SUCCESS_STATUS.includes(jobExecution?.statusCode)) {
                        const findedSuccessLog = jobLogs.find(jl => jl.status === SUCCESS && jl.value?.startsWith('{'))?.value
                        if (findedSuccessLog) {
                            setValidateImport(JSON.parse(findedSuccessLog))
                        }
                        nextStep()
                    }
                })
            }
        })
    }

    return (
        <StepperDialog
            steps={[{
                label: i18n.settings,
                constant: STEP.SETTINGS,
                nextAvailable: !!fileContent,
                onNext: nextStep => {
                    if (fileContent === prevFileContent) {
                        nextStep()
                    } else {
                        const param = { folderId }
                        const splittedFileName = fileName.split('.')
                        const parameters = new FileUploadInput({
                            stationType: 0,
                            stationId: 0,
                            path: 'DATA/PERITEC/',
                            name: splittedFileName?.[1] ? `ImportPerimetresTemp.${splittedFileName[1]}` : fileName,
                            content: fileContent,
                        })

                        dispatch(IntegrationAction.integration(IMPORT_PERIMETERS_JOB_ID, param, parameters, false, undefined, false)).then(() => {
                            setPrevFileContent(fileContent)
                            automaticCallLogs(nextStep)
                        })
                        nextStep()
                    }
                },
            }, {
                label: i18n.status,
                constant: STEP.STATUS,
                nextAvailable: (!importLogs?.length || !importLogs.some(il => il.level === 1)) && hasValue(job?.statusCode),
            }, {
                label: i18n.success,
                constant: STEP.SUCCESS,
            }].filter(s => !!s)}
            open={open}
            title={i18n.importLabel}
            closeDialog={onClose}
            renderSaveButton={step => (step === STEP.SUCCESS) && (
                <ButtonMUI
                    variant='contained'
                    onClick={() => {
                        refreshData()
                        onClose()
                    }}
                >
                    {i18n.finish}
                </ButtonMUI>
            )}
        >
            {step => (
                <>
                    {step === STEP.SETTINGS && (
                        <ImportStep
                            onUploadFile={onImportFile}
                        />
                    )}
                    {step === STEP.STATUS && (
                        <CheckUpStep
                            importLogs={importLogs}
                            job={job}
                        />
                    )}
                    {step === STEP.SUCCESS && (
                        <ValidateStep
                            importLogs={importLogs}
                            validateImport={validateImport}
                        />
                    )}
                </>
            )}
        </StepperDialog>
    )
}

PerimetersImportStepper.propTypes = {
    folderId: PropTypes.number,
    open: PropTypes.bool,
    setOpen: PropTypes.func,
    refreshData: PropTypes.func,
}

export default PerimetersImportStepper